import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Card,
  Row,
  Col,
  Avatar,
  Tag,
  Divider,
  Space,
  Input,
  Select,
  Checkbox,
  InputNumber,
  message,
  Spin,
  Tabs,
  Badge,
  Statistic,
  Image,
  Upload,
  Alert
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
  StarOutlined,
  HeartOutlined,
  SafetyOutlined,
  TeamOutlined,
  CheckCircleFilled,
  BankOutlined,
  UploadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import './BeneficiaryProfile.css';
import ImageUpload from './ImageUpload';
import ImageCropUpload from './ImageCropUpload';
import CharityAppPreview from './CharityAppPreview';
import { beneficiaryAPI } from '../services/api';
import { BENEFICIARY_CATEGORIES } from '../constants/beneficiaryCategories';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface BeneficiaryProfileProps {
  beneficiaryId: string;
  beneficiaryData?: any; // Raw beneficiary data from API
  onClose: () => void;
  onUpdate: (updatedData: any) => void;
}

interface BeneficiaryData {
  id: string;
  beneficiaryName: string;
  contactName: string;
  email: string;
  contactNumber: string;
  bankAccount: string;
  donation: string;
  dateOfJoin: string;
  cityState: string;
  beneficiaryCause: string;
  beneficiaryType: string;
  donors: number;
  active: boolean;
  enabled: boolean;
  // New fields
  about?: string;
  // mainImageUrl is the charities.image_url column: the wide 2:1 cover across
  // the top of the profile. Named "main" from before there were three roles;
  // the label in the form says Cover.
  mainImageUrl?: string;
  logoUrl?: string;
  /** Square 1:1 crop shown on the browse list (charities.highlight_url). */
  highlightUrl?: string;
  additionalImages?: string[];
  videoUrl?: string;
  whyThisMatters?: string;
  successStory?: string;
  storyAuthor?: string;
  // Impact Metrics - NEW fields (now accept full sentences)
  livesImpacted?: string; // Text - full sentence describing impact (e.g., "Over 10,000 children have received life-saving treatment")
  programsActive?: string; // Text - full sentence describing active programs (e.g., "We operate 25 programs across 10 states")
  directToProgramsPercentage?: string; // Text - full sentence describing percentage (e.g., "95% of all donations go directly to programs")
  // Legacy fields (deprecated - replaced by new impact metrics)
  familiesHelped?: string;
  communitiesServed?: number;
  directToPrograms?: number;
  impactStatement1?: string;
  impactStatement2?: string;
  ein?: string;
  website?: string;
  form990Url?: string;
  verificationStatus?: boolean;
  /** Approved but hidden from donors until the profile is complete. */
  awaitingProfileCompletion?: boolean;
  volunteerInfo?: string;
  // Fields from spec
  latitude?: number | string;
  longitude?: number | string;
  location?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  likes?: number;
  mutual?: number;
  social?: string;
  isActive?: boolean;
}

const BeneficiaryProfile: React.FC<BeneficiaryProfileProps> = ({
  beneficiaryId,
  beneficiaryData: rawBeneficiaryData,
  onClose,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [beneficiaryData, setBeneficiaryData] = useState<BeneficiaryData | null>(null);
  const [formData, setFormData] = useState<any>({});
  // Additional images state
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);

  // Fetch full beneficiary details by ID
  useEffect(() => {
    const fetchBeneficiary = async () => {
      if (!beneficiaryId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Always fetch fresh data from API to ensure we have all fields
        const response = await beneficiaryAPI.getBeneficiary(parseInt(beneficiaryId));
        
        if (response.success && response.data) {
          // Use the fetched data instead of rawBeneficiaryData
          const apiData = response.data;
          transformAndSetData(apiData);
        } else if (rawBeneficiaryData) {
          // Fallback to passed data if API call fails
          transformAndSetData(rawBeneficiaryData);
        } else {
          message.error('Failed to load beneficiary details');
        }
      } catch (error) {
        console.error('Error fetching beneficiary:', error);
        // Fallback to passed data if available
        if (rawBeneficiaryData) {
          transformAndSetData(rawBeneficiaryData);
        } else {
          message.error('Failed to load beneficiary details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBeneficiary();
  }, [beneficiaryId]); // Only depend on beneficiaryId, not rawBeneficiaryData

  // Transform API data to display format
  const transformAndSetData = (apiData: any) => {
    
    // Transform API data to match our interface
    const transformed: BeneficiaryData = {
        id: apiData.id?.toString() || beneficiaryId,
        beneficiaryName: apiData.name || apiData.beneficiaryName || 'Unknown',
        contactName: apiData.contact_name || apiData.contactName || '',
        email: apiData.email || apiData.primary_email || apiData.primaryEmail || apiData.contact_email || apiData.contactEmail || '',
        contactNumber: apiData.phone || apiData.contactNumber || apiData.phoneNumber || '',
        bankAccount: apiData.bank_account || apiData.bankAccount || '',
        donation: apiData.total_donations ? `$${apiData.total_donations.toLocaleString()}` : apiData.donation || '$0',
        dateOfJoin: apiData.createdAt || apiData.created_at || apiData.dateOfJoin || '',
        cityState: apiData.location || 
                   (apiData.city && apiData.state ? `${apiData.city}, ${apiData.state}` : '') ||
                   (apiData.address ? `${apiData.address.city || ''}, ${apiData.address.state || ''}`.replace(/^,\s*|,\s*$/g, '') : '') ||
                   apiData.cityState || '',
        // Store separate city/state for editing
        city: apiData.city || '',
        state: apiData.state || '',
        zipCode: apiData.zip_code || apiData.zipCode || '',
        beneficiaryCause: apiData.category || apiData.cause || apiData.beneficiaryCause || '',
        beneficiaryType: apiData.type || apiData.beneficiaryType || '',
        donors: apiData.donor_count || apiData.mutual || apiData.donors || 0,
        active: apiData.isActive !== undefined ? apiData.isActive : 
                (apiData.is_active !== undefined ? apiData.is_active : true),
        enabled: apiData.isActive !== undefined ? apiData.isActive : 
                 (apiData.is_active !== undefined ? apiData.is_active : true),
        // Additional fields
        about: apiData.about || apiData.description || '',
        mainImageUrl: apiData.imageUrl || apiData.main_image || apiData.main_image_url || apiData.mainImageUrl || '',
        logoUrl: apiData.logo || apiData.logo_url || apiData.logoUrl || '',
        // Not falling back to imageUrl here. The API already resolves
        // highlightUrl -> image_url -> logo_url for the app, so accepting
        // that fallback into the form would make an uncropped cover look
        // like a saved highlight crop, and saving would write it in as one.
        highlightUrl: apiData.highlight_url || apiData.highlightUrl || '',
        // Additional images - can be array or comma-separated string
        // image_urls is the column the donor app renders. This form previously
        // read and wrote `additional_images`, which the API neither returns nor
        // accepts — so uploads went to storage and the URL list was silently
        // dropped. Falls back to the old field so anything that did land there
        // is not lost.
        additionalImages: (() => {
          const src = apiData.imageUrls ?? apiData.image_urls ?? apiData.additional_images;
          if (Array.isArray(src)) return src.filter((img: any) => img);
          if (typeof src === 'string' && src.trim()) {
            return src.split(',').map((i: string) => i.trim()).filter(Boolean);
          }
          return [];
        })(),
        whyThisMatters: apiData.why_this_matters || apiData.mission || apiData.whyThisMatters || '',
        successStory: apiData.success_story || apiData.successStory || '',
        storyAuthor: apiData.story_author || apiData.storyAuthor || '',
        // Impact Metrics - NEW fields
        livesImpacted: apiData.lives_impacted || apiData.livesImpacted || null,
        programsActive: apiData.programs_active || apiData.programsActive || null,
        directToProgramsPercentage: apiData.direct_to_programs_percentage || apiData.directToProgramsPercentage || null,
        // Legacy fields (for backward compatibility)
        familiesHelped: apiData.families_helped || apiData.familiesHelped || '',
        communitiesServed: apiData.communities_served || apiData.communitiesServed || 0,
        directToPrograms: apiData.direct_to_programs || apiData.directToPrograms || 0,
        impactStatement1: apiData.impact_statement_1 || apiData.impactStatement1 || '',
        impactStatement2: apiData.impact_statement_2 || apiData.impactStatement2 || '',
        ein: apiData.ein || '',
        website: apiData.website || '',
        videoUrl: apiData.videoUrl || apiData.video_url || '',
        form990Url: apiData.form_990_url || apiData.form990Url || '',
        verificationStatus: apiData.verification_status || apiData.verificationStatus || false,
        // Approved but hidden from donors until the profile is filled in.
        awaitingProfileCompletion:
          apiData.awaitingProfileCompletion ?? apiData.awaiting_profile_completion ?? false,
        volunteerInfo: apiData.volunteer_info || apiData.volunteerInfo || '',
        // New fields from spec
        latitude: apiData.latitude || '',
        longitude: apiData.longitude || '',
        location: apiData.location || '',
        likes: apiData.likes || 0,
        mutual: apiData.mutual || 0,
        social: apiData.social || '',
        isActive: apiData.isActive !== undefined ? apiData.isActive : 
                  (apiData.is_active !== undefined ? apiData.is_active : true),
      };
      
      
      // Set additional images state before setting formData
      setAdditionalImages(transformed.additionalImages || []);
      
      setBeneficiaryData(transformed);
      setFormData(transformed);
      
  };

  const handleEdit = () => {
    setIsEditing(true);
    const newFormData = { ...beneficiaryData };
    setFormData(newFormData);
    
    // VERIFY Impact & Story data is in formData when entering edit mode
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({ ...beneficiaryData });
    // Reset additional images to original state
    if (beneficiaryData) {
      const additionalImgs = Array.isArray(beneficiaryData.additionalImages) 
        ? beneficiaryData.additionalImages 
        : [];
      setAdditionalImages(additionalImgs);
    }
  };
  
  // Handle additional image changes
  const handleAdditionalImageChange = (url: string | null, index: number) => {
    if (url) {
      const newImages = [...additionalImages];
      newImages[index] = url;
      setAdditionalImages(newImages);
      // Update formData as well
      handleInputChange('additionalImages', newImages.filter(img => img));
    } else {
      const newImages = additionalImages.filter((_, i) => i !== index);
      setAdditionalImages(newImages);
      handleInputChange('additionalImages', newImages);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Transform form data to match backend API format
      // CRITICAL: Backend requires name field - send all three variations for compatibility
      const charityName = formData.beneficiaryName?.trim() || '';
      
      
      if (!charityName) {
        console.error('❌ Charity name is missing or empty during update!', {
          formDataKeys: Object.keys(formData),
          beneficiaryName: formData.beneficiaryName
        });
        message.error('Charity name is required. Please enter the organization name.');
        setSaving(false);
        return;
      }
      
      // DEBUG: Log formData before creating updateData
      
      const updateData: any = {
        // CRITICAL: Send name in all three formats backend accepts to ensure compatibility
        name: charityName,
        beneficiaryName: charityName,
        charityName: charityName,
        category: formData.beneficiaryCause || '',
        type: formData.beneficiaryType || '',
        location: formData.location || formData.cityState || '',
        // Location breakdown - try to extract from location string or use cityState
        // Also check if we have separate city/state fields in formData
        city: formData.city || (() => {
          const loc = formData.location || formData.cityState || '';
          if (loc.includes(',')) {
            return loc.split(',')[0]?.trim() || '';
          }
          return '';
        })(),
        state: formData.state || (() => {
          const loc = formData.location || formData.cityState || '';
          if (loc.includes(',')) {
            return loc.split(',')[1]?.trim() || '';
          }
          return '';
        })(),
        zip_code: formData.zipCode || formData.zip_code || '',
        // Contact fields - send null instead of empty string if not provided
        // Backend might ignore empty strings, so we send null to ensure update
        phone: formData.contactNumber && formData.contactNumber.trim() ? formData.contactNumber.trim() : null,
        contact_name: formData.contactName && formData.contactName.trim() ? formData.contactName.trim() : null,
        // Try sending email - backend may have added this column
        email: formData.email && formData.email.trim() ? formData.email.trim() : null,
        primary_email: formData.email && formData.email.trim() ? formData.email.trim() : null, // Also try primary_email
      
      // Log contact fields being sent
      // (temporary logging to debug)
        // Use exact database field names (snake_case)
        about: formData.about || '',
        // Impact & Story fields - CRITICAL: Send in both formats for backend compatibility
        // Get the value from either camelCase or snake_case form field
        why_this_matters: (formData.whyThisMatters && formData.whyThisMatters.trim()) || 
                          (formData.why_this_matters && formData.why_this_matters.trim()) || 
                          '',
        whyThisMatters: (formData.whyThisMatters && formData.whyThisMatters.trim()) || 
                        (formData.why_this_matters && formData.why_this_matters.trim()) || 
                        '', // Also send camelCase
        success_story: (formData.successStory && formData.successStory.trim()) || 
                       (formData.success_story && formData.success_story.trim()) || 
                       '',
        successStory: (formData.successStory && formData.successStory.trim()) || 
                      (formData.success_story && formData.success_story.trim()) || 
                      '', // Also send camelCase
        story_author: (formData.storyAuthor && formData.storyAuthor.trim()) || 
                      (formData.story_author && formData.story_author.trim()) || 
                      '',
        storyAuthor: (formData.storyAuthor && formData.storyAuthor.trim()) || 
                     (formData.story_author && formData.story_author.trim()) || 
                     '', // Also send camelCase
        // Impact Metrics - NEW fields (now accept full sentences as text)
        // Send both camelCase and snake_case for backend compatibility
        // Only send if non-empty strings
        livesImpacted: formData.livesImpacted && formData.livesImpacted.trim() ? formData.livesImpacted.trim() : null,
        lives_impacted: formData.livesImpacted && formData.livesImpacted.trim() ? formData.livesImpacted.trim() : null,
        programsActive: formData.programsActive && formData.programsActive.trim() ? formData.programsActive.trim() : null,
        programs_active: formData.programsActive && formData.programsActive.trim() ? formData.programsActive.trim() : null,
        directToProgramsPercentage: formData.directToProgramsPercentage && formData.directToProgramsPercentage.trim() ? formData.directToProgramsPercentage.trim() : null,
        direct_to_programs_percentage: formData.directToProgramsPercentage && formData.directToProgramsPercentage.trim() ? formData.directToProgramsPercentage.trim() : null,
        // NOTE: The following fields may not exist in backend schema - removed to prevent 400 errors
        // verification_status: formData.verificationStatus !== undefined ? formData.verificationStatus : true, // ⚠️ DOES NOT EXIST - causing 400 error
        // Impact Statements - send in both formats for backend compatibility
        // Only send if they have non-empty values (send null instead of empty string)
        impact_statement_1: (formData.impactStatement1 && formData.impactStatement1.trim()) || 
                            (formData.impact_statement_1 && formData.impact_statement_1.trim()) || 
                            null,
        impactStatement1: (formData.impactStatement1 && formData.impactStatement1.trim()) || 
                          (formData.impact_statement_1 && formData.impact_statement_1.trim()) || 
                          null, // Also send camelCase
        impact_statement_2: (formData.impactStatement2 && formData.impactStatement2.trim()) || 
                            (formData.impact_statement_2 && formData.impact_statement_2.trim()) || 
                            null,
        impactStatement2: (formData.impactStatement2 && formData.impactStatement2.trim()) || 
                          (formData.impact_statement_2 && formData.impact_statement_2.trim()) || 
                          null, // Also send camelCase
        ein: formData.ein || '',
        website: formData.website || '',
        social: formData.social || '',
        likes: formData.likes || 0,
        mutual: formData.mutual || 0,
        is_active: formData.isActive !== undefined ? formData.isActive : true,
        isActive: formData.isActive !== undefined ? formData.isActive : true, // Send both for compatibility
        // Backend expects imageUrl (camelCase) which saves to image_url column
        imageUrl: formData.mainImageUrl || '', // ⚠️ CRITICAL: Backend expects this field name
        main_image: formData.mainImageUrl || '',
        main_image_url: formData.mainImageUrl || '', // Send both for compatibility
        // Backend expects logoUrl (camelCase) which saves to logo_url column
        logoUrl: formData.logoUrl || '', // ⚠️ CRITICAL: Backend expects this field name
        logo: formData.logoUrl || '',
        logo_url: formData.logoUrl || '', // Send both for compatibility
        // Square crop for the browse list. null, not '', when unset: the column
        // is the record of which charities have actually been re-cropped, and
        // an empty string renders the same as null while still counting as a
        // value, so "WHERE highlight_url IS NULL" would stop finding the ones
        // that still need doing. Clearing it also sends null, which puts the
        // charity back on the fallback to the cover.
        highlightUrl: formData.highlightUrl || null,
        highlight_url: formData.highlightUrl || null,
        // Additional images - send as array, filtered to remove empty slots
        // Max 5, matching the charities_image_urls_max_5 CHECK constraint.
        imageUrls: additionalImages.filter((img) => img && img.trim()).slice(0, 5),
        videoUrl: (formData.videoUrl || '').trim(),
        volunteer_info: formData.volunteerInfo || ''
      };

      // Explicitly remove fields that don't exist in backend schema
      // These fields cause 400 errors if included
      const fieldsToRemove = [
        'verification_status', // ⚠️ DOES NOT EXIST - causing 400 error
        'verificationStatus', // camelCase version
        'communities_served', // OLD field - replaced by programs_active
        'families_helped', // OLD field - replaced by lives_impacted
        'direct_to_programs', // OLD field - replaced by direct_to_programs_percentage
        // Impact statements - REMOVED from removal list (backend now supports them)
        // 'impact_statement_1', // Now supported
        // 'impact_statement_2', // Now supported
        'transparency_rating',
        'communitiesServed', // OLD camelCase version
        'familiesHelped', // OLD camelCase version
        'directToPrograms' // OLD camelCase version (without Percentage suffix)
      ];
      
      fieldsToRemove.forEach(field => {
        if (updateData.hasOwnProperty(field)) {
          delete updateData[field];
        }
      });
      
      // Verify fields are removed
      const hasRemovedFields = fieldsToRemove.some(field => updateData.hasOwnProperty(field));
      if (hasRemovedFields) {
        console.error('❌ CRITICAL: Some removed fields are still in payload!', fieldsToRemove.filter(f => updateData.hasOwnProperty(f)));
      }

      
      // VERIFY Impact & Story fields are in payload
      

      // Call API to update beneficiary
      
      const response = await beneficiaryAPI.updateBeneficiary(parseInt(beneficiaryId), updateData);
      
      
      // Handle different response formats
      // Backend might return: { success: true, data: {...} } OR just the data directly OR { id: ... }
      const responseData = response.data || response;
      const isSuccess = response.success !== false; // Default to true if not specified
      
      
      if (responseData) {
        if (typeof responseData === 'object') {
          // Check if impact statements are in the response
        }
      }

      if (isSuccess) {
        // Refresh the data by fetching again
        const fetchResponse = await beneficiaryAPI.getBeneficiary(parseInt(beneficiaryId));
        
        if (fetchResponse.success && fetchResponse.data) {
          transformAndSetData(fetchResponse.data);
        } else if (fetchResponse.data) {
          // Handle case where response.data exists but success is not set
          transformAndSetData(fetchResponse.data);
        } else {
          // Fallback: update local state
          setBeneficiaryData(formData);
        }
        
        setIsEditing(false);
        onUpdate(formData);
        if ((responseData as any)?.wentLive) {
          // The save closed the last gap, so the charity just became visible.
          message.success({
            content: `${formData.name || 'Beneficiary'} is now live and visible to donors.`,
            duration: 8,
          });
        } else {
          const stillMissing: string[] = Array.isArray((responseData as any)?.missingFields)
            ? (responseData as any).missingFields
            : [];
          if (stillMissing.length) {
            message.warning({
              content: `Saved, but still hidden from donors — needs: ${stillMissing.join(', ')}.`,
              duration: 8,
            });
          } else {
            message.success('Beneficiary updated successfully!');
          }
        }
      } else {
        const errorMsg = response.error || responseData?.error || 'Failed to update beneficiary';
        console.error('❌ Update error:', errorMsg);
        message.error(`Failed to update beneficiary: ${errorMsg}`);
      }
    } catch (error: any) {
      console.error('❌ Error updating beneficiary:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        status: error?.status,
        response: error?.response,
        stack: error?.stack
      });
      const errorMsg = error?.message || 'Failed to update beneficiary. Please try again.';
      message.error(errorMsg);
      // Don't close editing mode if there's an error - let user try again
      // setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Special logging for Impact & Story fields
      if (field === 'whyThisMatters' || field === 'successStory' || field === 'storyAuthor') {
      }
      
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="beneficiary-profile-loading">
        <Spin size="large" />
        <Text>Loading beneficiary profile...</Text>
      </div>
    );
  }

  if (!beneficiaryData) {
    return (
      <div className="beneficiary-profile-error">
        <Text>Beneficiary not found</Text>
        <Button onClick={onClose}>Go Back</Button>
      </div>
    );
  }

  const renderBasicInfo = () => (
    <Card title="Basic Information" className="profile-section-card">
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Beneficiary Name</label>
            {isEditing ? (
              <Input
                value={formData.beneficiaryName}
                onChange={(e) => handleInputChange('beneficiaryName', e.target.value)}
                placeholder="Enter beneficiary name"
              />
            ) : (
              <Text strong>{beneficiaryData.beneficiaryName}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Category</label>
            {isEditing ? (
              <Select
                value={formData.beneficiaryCause}
                onChange={(value) => handleInputChange('beneficiaryCause', value)}
                placeholder="Select category"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
              >
                {BENEFICIARY_CATEGORIES.map((category) => (
                  <Option key={category.value} value={category.value} title={category.description}>
                    {category.label}
                  </Option>
                ))}
              </Select>
            ) : (
              <Tag color="blue">{beneficiaryData.beneficiaryCause}</Tag>
            )}
          </div>
        </Col>
      </Row>
      
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Location</label>
            {isEditing ? (
              <Input
                value={formData.location || formData.cityState}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="City, State"
              />
            ) : (
              <Text>{beneficiaryData.location || beneficiaryData.cityState}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Beneficiary Type</label>
            {isEditing ? (
              <Select
                value={formData.beneficiaryType}
                onChange={(value) => handleInputChange('beneficiaryType', value)}
                placeholder="Select type"
                style={{ width: '100%' }}
              >
                <Option value="Large">Large</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Small">Small</Option>
              </Select>
            ) : (
              <Tag color="green">{beneficiaryData.beneficiaryType}</Tag>
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Latitude</label>
            {isEditing ? (
              <InputNumber
                value={formData.latitude}
                onChange={(value) => handleInputChange('latitude', value)}
                placeholder="e.g., 33.7490"
                style={{ width: '100%' }}
                precision={8}
                min={-90}
                max={90}
              />
            ) : (
              <Text>{beneficiaryData.latitude || 'N/A'}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Longitude</label>
            {isEditing ? (
              <InputNumber
                value={formData.longitude}
                onChange={(value) => handleInputChange('longitude', value)}
                placeholder="e.g., -84.3880"
                style={{ width: '100%' }}
                precision={8}
                min={-180}
                max={180}
              />
            ) : (
              <Text>{beneficiaryData.longitude || 'N/A'}</Text>
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Likes</label>
            {isEditing ? (
              <InputNumber
                value={formData.likes}
                onChange={(value) => handleInputChange('likes', value)}
                placeholder="0"
                style={{ width: '100%' }}
                min={0}
              />
            ) : (
              <Text>{beneficiaryData.likes || 0}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Mutual Connections</label>
            {isEditing ? (
              <InputNumber
                value={formData.mutual}
                onChange={(value) => handleInputChange('mutual', value)}
                placeholder="0"
                style={{ width: '100%' }}
                min={0}
              />
            ) : (
              <Text>{beneficiaryData.mutual || 0}</Text>
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Social Media</label>
            {isEditing ? (
              <Input
                value={formData.social}
                onChange={(e) => handleInputChange('social', e.target.value)}
                placeholder="@organizationname"
                maxLength={100}
              />
            ) : (
              <Text>{beneficiaryData.social || 'N/A'}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Active Status</label>
            {isEditing ? (
              <Checkbox
                checked={formData.isActive !== false}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              >
                Active (show in app)
              </Checkbox>
            ) : (
              <Tag color={beneficiaryData.isActive !== false ? 'success' : 'default'}>
                {beneficiaryData.isActive !== false ? 'Active' : 'Inactive'}
              </Tag>
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Primary Contact</label>
            {isEditing ? (
              <Input
                value={formData.contactName || ''}
                onChange={(e) => {
                  handleInputChange('contactName', e.target.value);
                }}
                placeholder="Enter contact name"
              />
            ) : (
              <Text>{beneficiaryData.contactName || <Text type="secondary" style={{ fontStyle: 'italic' }}>Not provided</Text>}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Email</label>
            {(() => {
              return null;
            })()}
            {isEditing ? (
              <Input
                value={formData?.email || ''}
                onChange={(e) => {
                  handleInputChange('email', e.target.value);
                }}
                placeholder="Enter email"
              />
            ) : (
              <Text>{beneficiaryData?.email || <Text type="secondary" style={{ fontStyle: 'italic' }}>Not provided</Text>}</Text>
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>Phone Number</label>
            {isEditing ? (
              <Input
                value={formData.contactNumber || ''}
                onChange={(e) => {
                  handleInputChange('contactNumber', e.target.value);
                }}
                placeholder="Enter phone number"
              />
            ) : (
              <Text>{beneficiaryData.contactNumber || <Text type="secondary" style={{ fontStyle: 'italic' }}>Not provided</Text>}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Bank Account</label>
            {isEditing ? (
              <Input
                value={formData.bankAccount}
                onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                placeholder="Enter bank account"
              />
            ) : (
              <Text>{beneficiaryData.bankAccount || <Text type="secondary" style={{ fontStyle: 'italic' }}>Not provided</Text>}</Text>
            )}
          </div>
        </Col>
      </Row>

      <div className="form-field">
        <label>About</label>
        {isEditing ? (
          <Input.TextArea
            value={formData.about}
            onChange={(e) => handleInputChange('about', e.target.value)}
            rows={4}
            placeholder="Describe the beneficiary organization and their mission..."
            showCount={{ formatter: ({ count, maxLength }) => `${count}/${maxLength}` }}
            maxLength={1000}
          />
        ) : (
          <Paragraph>{beneficiaryData.about || <Text type="secondary" style={{ fontStyle: 'italic' }}>Not set</Text>}</Paragraph>
        )}
      </div>

      <Divider />

      {/* Charity images.
          Three roles because the app has three differently-shaped slots, and
          one photo cannot fill all three without something being cut off. The
          cropper writes each one at its own aspect ratio, so the app never has
          to centre-crop and guess. See CLAUDE.md "Charity images". */}
      <div className="form-field">
        <label>Charity Images</label>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
          Each of these appears in a different place in the app, at a different
          shape. Upload once and position it; you can reframe any of them later
          without starting over.
        </Text>

        {isEditing ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Logo
              </Text>
              <ImageCropUpload
                role="logo"
                currentImageUrl={formData.logoUrl}
                onImageChange={(url) => handleInputChange('logoUrl', url || '')}
                bucketName="charity-images"
              />
            </Col>

            <Col xs={24} md={8}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Highlight
              </Text>
              <ImageCropUpload
                role="highlight"
                currentImageUrl={formData.highlightUrl}
                onImageChange={(url) => handleInputChange('highlightUrl', url || '')}
                bucketName="charity-images"
              />
            </Col>

            <Col xs={24} md={8}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Cover
              </Text>
              <ImageCropUpload
                role="cover"
                currentImageUrl={formData.mainImageUrl}
                onImageChange={(url) => handleInputChange('mainImageUrl', url || '')}
                bucketName="charity-images"
              />
            </Col>
          </Row>

        ) : (
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Logo</Text>
              {beneficiaryData.logoUrl ? (
                <Image
                  src={beneficiaryData.logoUrl}
                  alt="Charity logo"
                  width={96}
                  height={96}
                  style={{ objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : (
                <Text type="secondary">Not set</Text>
              )}
            </Col>
            <Col xs={24} md={8}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Highlight</Text>
              {beneficiaryData.highlightUrl ? (
                <Image
                  src={beneficiaryData.highlightUrl}
                  alt="Charity highlight image"
                  width={120}
                  height={120}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <Text type="secondary">
                  Not set, so the browse list is showing a centre-crop of the cover
                </Text>
              )}
            </Col>
            <Col xs={24} md={8}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Cover</Text>
              {beneficiaryData.mainImageUrl ? (
                <Image
                  src={beneficiaryData.mainImageUrl}
                  alt="Charity cover image"
                  width={240}
                  height={120}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <Text type="secondary">Not set</Text>
              )}
            </Col>
          </Row>
        )}
        {/* Live preview, at the app's real dimensions. Reads formData rather
            than beneficiaryData so a crop shows up here the moment it is made,
            without waiting for a save. */}
        {/* Tinted, so it reads as a window into the app rather than more form
            to fill in. Everything above this line is editable and everything
            inside it is not, and that difference should be visible before
            anyone tries to click a preview. */}
        <div
          style={{
            marginTop: 28,
            padding: 20,
            background: '#EFF5F7',
            border: '1px solid #D9E7EC',
            borderRadius: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 4,
              flexWrap: 'wrap',
            }}
          >
            <Title level={5} style={{ margin: 0, color: '#21555b' }}>
              How this looks in the app
            </Title>
            <Tag
              icon={<EyeOutlined />}
              style={{
                background: '#fff',
                borderColor: '#BBD6DE',
                color: '#21555b',
                marginInlineEnd: 0,
                fontSize: 11,
              }}
            >
              Preview only
            </Tag>
          </div>
          <Text style={{ display: 'block', marginBottom: 16, fontSize: 12, color: '#5B7680' }}>
            Shown at the real size donors see. Crop above and this updates straight away.
          </Text>
          <CharityAppPreview
            data={{
              name: formData.beneficiaryName || beneficiaryData.beneficiaryName,
              category: formData.beneficiaryCause || beneficiaryData.beneficiaryCause,
              location: formData.cityState || beneficiaryData.cityState,
              logoUrl: formData.logoUrl,
              highlightUrl: formData.highlightUrl,
              coverUrl: formData.mainImageUrl,
              isPendingVerification: (beneficiaryData as any).isPendingVerification,
            }}
          />
        </div>
      </div>

      <Divider />

      <div className="form-field">
        <label>Additional Images (Optional)</label>
        <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: '12px' }}>
          Upload up to 5 photos showcasing your programs and impact. These appear
          as a photo strip on the charity's profile in the app.
        </Text>
        {isEditing ? (
          <Row gutter={[16, 16]}>
            {[0, 1, 2, 3, 4].map((index) => (
              <Col span={8} key={index}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Image {index + 1}
                  </Text>
                  <ImageUpload
                    currentImageUrl={additionalImages[index] || undefined}
                    onImageChange={(url) => handleAdditionalImageChange(url, index)}
                    title="Upload Image"
                    description="Click or drag to upload"
                  />
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <Row gutter={[16, 16]}>
            {beneficiaryData.additionalImages && beneficiaryData.additionalImages.length > 0 ? (
              beneficiaryData.additionalImages.map((imgUrl: string, index: number) => (
                <Col span={8} key={index}>
                  <Image
                    src={imgUrl}
                    alt={`Additional image ${index + 1}`}
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Col>
              ))
            ) : (
              <Col span={24}>
                <Text type="secondary">No additional images uploaded</Text>
              </Col>
            )}
          </Row>
        )}
      </div>

      <Divider />

      <div className="form-field">
        <label>Video (Optional)</label>
        <Text type="secondary" style={{ display: 'block', marginBottom: '12px', fontSize: '12px' }}>
          Paste a YouTube or Vimeo link, or a direct link to an .mp4 file. A
          direct file plays inline in the app; a YouTube or Vimeo link opens in
          the browser. Leave blank to remove the video.
        </Text>
        {isEditing ? (
          <Input
            value={formData.videoUrl || ''}
            onChange={(e) => handleInputChange('videoUrl', e.target.value)}
            placeholder="https://www.youtube.com/watch?v=… or https://…/story.mp4"
          />
        ) : (
          beneficiaryData.videoUrl ? (
            <a href={beneficiaryData.videoUrl} target="_blank" rel="noreferrer">
              {beneficiaryData.videoUrl}
            </a>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>No video added</Text>
          )
        )}
      </div>
    </Card>
  );

  const renderTrustTransparency = () => (
    <Card title="Trust & Transparency" className="profile-section-card">
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <div className="form-field">
            <label>EIN Number</label>
            {isEditing ? (
              <Input
                value={formData.ein}
                onChange={(e) => handleInputChange('ein', e.target.value)}
                placeholder="e.g., 12-3456789"
              />
            ) : (
              <Text>{beneficiaryData.ein}</Text>
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="form-field">
            <label>Website</label>
            {isEditing ? (
              <Input
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://example.org"
              />
            ) : (
              <Text>
                {beneficiaryData.website && (
                  <a href={beneficiaryData.website} target="_blank" rel="noopener noreferrer">
                    {beneficiaryData.website}
                  </a>
                )}
              </Text>
            )}
          </div>
        </Col>
      </Row>

      <div className="form-field verification-status">
        {isEditing ? (
          <div className="verification-status-inline">
            <label>Verification Status</label>
            <Checkbox
              checked={formData.verificationStatus}
              onChange={(e) => handleInputChange('verificationStatus', e.target.checked)}
            >
              Verified 501(c)(3) Nonprofit
            </Checkbox>
          </div>
        ) : (
          <>
            <label>Verification Status</label>
            <Badge
              status={beneficiaryData.verificationStatus ? 'success' : 'default'}
              text={beneficiaryData.verificationStatus ? 'Verified' : 'Not Verified'}
            />
          </>
        )}
      </div>

      {/* Form-990 upload - visible when editing (#89) */}
      {(isEditing || beneficiaryData?.form990Url) && (
        <div className="form-field" style={{ marginTop: 16 }}>
          <label>Form-990</label>
          {isEditing ? (
            <>
              <Upload
                beforeUpload={(file) => {
                  handleInputChange('form990File', file);
                  return false; // Prevent auto upload - handled in save
                }}
                onRemove={() => handleInputChange('form990File', null)}
                maxCount={1}
                accept=".pdf,.doc,.docx"
              >
                <Button icon={<UploadOutlined />} className="upload-btn">
                  Select Form-990 File
                </Button>
              </Upload>
              {beneficiaryData?.form990Url && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Current: <a href={beneficiaryData.form990Url} target="_blank" rel="noopener noreferrer">View existing Form-990</a>
                </Text>
              )}
            </>
          ) : (
            beneficiaryData?.form990Url ? (
              <a href={beneficiaryData.form990Url} target="_blank" rel="noopener noreferrer">View Form-990</a>
            ) : (
              <Text type="secondary">No Form-990 uploaded</Text>
            )
          )}
        </div>
      )}
    </Card>
  );

  const renderVolunteerInfo = () => (
    <Card title="Get Involved" className="profile-section-card">
      <div className="form-field">
        <label>Volunteer Information</label>
        {isEditing ? (
          <Input.TextArea
            value={formData.volunteerInfo}
            onChange={(e) => handleInputChange('volunteerInfo', e.target.value)}
            rows={4}
            placeholder="Information about volunteer opportunities..."
            showCount={{ formatter: ({ count, maxLength }) => `${count}/${maxLength}` }}
            maxLength={500}
          />
        ) : (
          <Paragraph>{beneficiaryData.volunteerInfo}</Paragraph>
        )}
      </div>
    </Card>
  );

  const renderStats = () => (
    <Card title="Quick Stats" className="profile-section-card">
      <Row gutter={[24, 16]}>
        <Col span={6}>
          <Statistic
            title="Total Donations"
            value={beneficiaryData.donation}
            prefix={<BankOutlined />}
            valueStyle={{ color: '#DB8633' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Donors"
            value={beneficiaryData.donors}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Date Joined"
            value={beneficiaryData.dateOfJoin}
            prefix={<StarOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Status"
            value={beneficiaryData.active ? 'Active' : 'Inactive'}
            prefix={beneficiaryData.active ? <CheckCircleFilled /> : <CloseOutlined />}
            valueStyle={{ color: beneficiaryData.active ? '#52c41a' : '#ff4d4f' }}
          />
        </Col>
      </Row>
    </Card>
  );

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      icon: <UserOutlined />,
      children: (
        <div className="tab-content">
          {renderStats()}
          {renderBasicInfo()}
        </div>
      )
    },
    // "Impact & Story" tab removed 2026-08-28, and its render function deleted
    // 2026-08-31. The donor app no longer shows whyThisMatters / successStory /
    // impact statements / impact metrics, so asking admins to fill them
    // produced data nothing displayed. The DB columns and existing values are
    // untouched — only the editor is gone.
    {
      key: 'trust',
      label: 'Trust & Transparency',
      icon: <SafetyOutlined />,
      children: (
        <div className="tab-content">
          {renderTrustTransparency()}
        </div>
      )
    },
    {
      key: 'volunteer',
      label: 'Get Involved',
      icon: <TeamOutlined />,
      children: (
        <div className="tab-content">
          {renderVolunteerInfo()}
        </div>
      )
    }
  ];

  return (
    <div className="beneficiary-profile-overlay">
      <div className="beneficiary-profile-container">
        {/* Approved but held back: donors cannot see this charity yet. Shown
            above the header so it is the first thing an admin reads, with the
            specific gaps rather than a vague "incomplete". */}
        {beneficiaryData.awaitingProfileCompletion && (
          <Alert
            type="warning"
            showIcon
            style={{ margin: '16px 16px 0' }}
            message="Not visible to donors yet"
            description={
              <>
                This charity is approved and any held donations have been
                released, but it stays hidden until the profile is complete.
                Fill in the missing details and save — saving is what makes it
                live.
                {(() => {
                  const d: any = beneficiaryData;
                  const gaps: string[] = [];
                  if (!String(d.beneficiaryCause || d.category || '').trim()) gaps.push('Category');
                  const t = String(d.type || '').trim().toLowerCase();
                  if (!t || t === 'pending') gaps.push('Type');
                  const isPlaceholder = (v: unknown) =>
                    String(v ?? '').toLowerCase().includes('pending verification by the thrive team');
                  const realAbout = String(d.about || '').trim() && !isPlaceholder(d.about);
                  const realDesc = String(d.description || '').trim() && !isPlaceholder(d.description);
                  if (!realAbout && !realDesc) gaps.push('About / description');
                  if (!String(d.imageUrl || d.logoUrl || '').trim()) gaps.push('Hero image or logo');
                  if (!String(d.website || '').trim()) gaps.push('Website');
                  if (!String(d.location || d.cityState || '').trim()) gaps.push('Location');
                  return gaps.length ? <div style={{ marginTop: 8 }}><strong>Still needed:</strong> {gaps.join(', ')}</div> : null;
                })()}
              </>
            }
          />
        )}
        {/* Header */}
        <div className="profile-header">
          <div className="header-left">
            <Button
              onClick={onClose}
              className="back-button"
            >
              Back to Beneficiaries
            </Button>
            <div className="beneficiary-title">
              <Avatar size={64} style={{ backgroundColor: '#DB8633' }}>
                {beneficiaryData.beneficiaryName.charAt(0)}
              </Avatar>
              <div className="title-content">
                <Title level={2} style={{ margin: 0 }}>
                  {beneficiaryData.beneficiaryName}
                </Title>
                <Text type="secondary">
                  {beneficiaryData.beneficiaryCause} • {beneficiaryData.cityState}
                </Text>
              </div>
            </div>
          </div>
          
          <div className="header-actions">
            {isEditing ? (
              <Space>
                <Button onClick={handleCancel} icon={<CloseOutlined />}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSave();
                  }}
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={saving}
                >
                  Save Changes
                </Button>
              </Space>
            ) : (
              <Button
                type="primary"
                onClick={handleEdit}
                icon={<EditOutlined />}
              >
                Edit Profile
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="profile-content">
          <Tabs
            items={tabItems}
            defaultActiveKey="overview"
            className="profile-tabs"
            size="large"
          />
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryProfile;
